"use client";

import {
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useId,
  useState,
} from "react";
import { X } from "lucide-react";
import { Button } from "./Button";

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const listener = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);
  return prefersReducedMotion;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
}

const sizeStyles = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-4xl",
};

function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "md",
  showCloseButton = true,
  closeOnOverlayClick = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const descriptionId = useId();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Use a ref for the handler so the effect doesn't re-run when onClose changes
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onCloseRef.current();
      return;
    }

    // Tab focus trap
    if (event.key !== "Tab" || !modalRef.current) return;

    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "textarea:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    const focusableElements = Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors),
    );

    if (focusableElements.length === 0) {
      event.preventDefault();
      modalRef.current.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []); // stable — reads onClose via ref

  // Focus trap and body scroll lock — runs only when isOpen changes
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);

      // Focus the modal container once on open
      setTimeout(() => {
        modalRef.current?.focus();
      }, 0);

      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", handleKeyDown);
        previouslyFocusedElement.current?.focus();
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <Fragment>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-[var(--modal-backdrop)] backdrop-blur-[var(--modal-backdrop-blur)] ${prefersReducedMotion ? "" : "animate-[fade-in_200ms_ease-out]"}`}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? headingId : undefined}
        aria-describedby={description ? descriptionId : undefined}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`
            relative w-full max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain ${sizeStyles[size]}
            bg-card border border-border rounded-2xl
            [box-shadow:var(--modal-shadow)]
            ${prefersReducedMotion ? "" : "animate-[slide-up_300ms_ease-out]"}
            focus-ring
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-border">
              <div>
                {title && (
                  <h2
                    id={headingId}
                    className="font-display text-xl font-semibold text-foreground tracking-[-0.025em]"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id={descriptionId}
                    className="mt-1.5 text-sm text-muted-foreground leading-[1.55]"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button variant="icon" onClick={onClose} aria-label="Kapat">
                  <X className="w-5 h-5" aria-hidden />
                </Button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-6">{children}</div>
        </div>
      </div>
    </Fragment>
  );
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

function ModalFooter({ children, className = "" }: ModalFooterProps) {
  return (
    <div
      className={`
        flex items-center justify-end gap-2.5
        pt-4 mt-5 border-t border-border
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export { Modal, ModalFooter, type ModalProps };
